import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Sparkles, ChevronRight, ChevronLeft, Calendar, Mic, Download, X, ChevronDown, Users, Send, List, LayoutGrid, Plus, ImagePlus, Save, UserPlus } from 'lucide-react';
import axios from 'axios';
import AudioPlayer from './AudioPlayer';
import AudioErrorBoundary from './AudioErrorBoundary';
import { useNavigationDirection } from '../contexts/NavigationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getImageUrl = (entry, idx, variant = 'medium') => {
  if (entry.images && entry.images[idx]) {
    const img = entry.images[idx];
    // Use pre-signed URLs from API response
    if (variant === 'thumb' && img.thumbUrl) return img.thumbUrl;
    if (variant === 'medium' && img.mediumUrl) return img.mediumUrl;
    if (variant === 'full' && img.fullUrl) return img.fullUrl;
    // Fallback to proxy endpoint
    return `${API}/journal/images/${img.id}?variant=${variant}`;
  }
  const fn = entry.image_filenames?.[idx];
  return fn ? `${API}/journal/images/${fn}?variant=${variant}` : null;
};
const getImageId = (entry, idx) => entry.images?.[idx]?.id || entry.image_filenames?.[idx] || null;
const getDownloadUrl = (entry, idx) => {
  if (entry.images && entry.images[idx] && entry.images[idx].downloadUrl) {
    const url = entry.images[idx].downloadUrl;
    // If relative path, prepend backend URL
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  }
  const id = getImageId(entry, idx);
  return id ? `${API}/journal/images/${id}/download` : null;
};

// Direction-aware page variants for swipe transitions
const getPageVariants = (direction) => ({
  initial: { opacity: 0, x: direction * 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: direction * -30 }
});

const pageTransition = { duration: 0.25, ease: 'easeOut' };

function TimelinePage({ user }) {
  const { direction } = useNavigationDirection();
  const [entries, setEntries] = useState([]);
  const [extractedDates, setExtractedDates] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [expandedEntries, setExpandedEntries] = useState({});
  const [transcribingId, setTranscribingId] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null); // { imageUrl: string, downloadUrl: string|null }
  const [highlightedEntryId, setHighlightedEntryId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedDay, setSelectedDay] = useState(null);
  const [timelineView, setTimelineView] = useState('calendar'); // default to calendar
  const [showYearPicker, setShowYearPicker] = useState(false);
  const calendarTouchRef = useRef({ startX: 0, startY: 0 });
  const yearPickerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const dragRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [hasScrolledToCenter, setHasScrolledToCenter] = useState(false);
  const entriesRef = useRef(null);
  const [shareEntryId, setShareEntryId] = useState(null);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const [createEntryDay, setCreateEntryDay] = useState(null); // day number for creating entry
  const [createEntryType, setCreateEntryType] = useState(null); // 'journal' or 'story'
  const [createEntryContent, setCreateEntryContent] = useState('');
  const [isCreatingEntry, setIsCreatingEntry] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const MAX_PREVIEW_LENGTH = 450;
  
  const toggleEntryExpansion = (entryId) => {
    setExpandedEntries(prev => ({ ...prev, [entryId]: !prev[entryId] }));
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (entries.length > 0) {
      const params = new URLSearchParams(location.search);
      const targetEntry = params.get('entry');
      const yearParam = params.get('year');
      const monthParam = params.get('month');
      
      if (targetEntry) {
        const entry = entries.find(e => e.id === targetEntry);
        if (entry) {
          const d = new Date(entry.timestamp);
          setSelectedYear(d.getFullYear());
          setSelectedMonth(d.getMonth());
          // Scroll to and highlight the target entry after render
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="timeline-entry-${targetEntry}"]`);
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              setHighlightedEntryId(targetEntry);
              setTimeout(() => setHighlightedEntryId(null), 3000);
            }
          }, 500);
        }
      } else if (yearParam && monthParam) {
        // Navigate to specific year/month from search
        setSelectedYear(parseInt(yearParam, 10));
        setSelectedMonth(parseInt(monthParam, 10));
      }
    }
  }, [entries, location.search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const ts = Date.now();
      const [entriesRes, datesRes] = await Promise.all([
        axios.get(`${API}/journal?_t=${ts}`),
        axios.get(`${API}/timeline/extracted-dates`)
      ]);
      setEntries(entriesRes.data);
      setExtractedDates(datesRes.data);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTranscribeEntry = async (entryId) => {
    setTranscribingId(entryId);
    try {
      const response = await axios.post(`${API}/journal/${entryId}/transcribe`);
      if (response.data.success) {
        const storiesExtracted = response.data.stories_extracted || 0;
        if (storiesExtracted > 0) {
          alert(`Transcription complete! ${storiesExtracted} ${storiesExtracted === 1 ? 'story was' : 'stories were'} extracted.`);
        } else {
          alert('Transcription complete!');
        }
        await fetchData();
      }
    } catch (error) {
      console.error('Error transcribing entry:', error);
      alert('Failed to transcribe. Please try again.');
    } finally {
      setTranscribingId(null);
    }
  };

  const groupedByYear = useMemo(() => entries.reduce((acc, entry) => {
    const date = new Date(entry.timestamp);
    const year = date.getFullYear();
    const month = date.getMonth();
    if (!acc[year]) acc[year] = {};
    if (!acc[year][month]) acc[year][month] = [];
    acc[year][month].push(entry);
    return acc;
  }, {}), [entries]);

  const groupedExtractedDates = useMemo(() => extractedDates.reduce((acc, dateInfo) => {
    const year = dateInfo.year;
    const month = dateInfo.month - 1;
    if (!acc[year]) acc[year] = {};
    if (!acc[year][month]) acc[year][month] = [];
    acc[year][month].push(dateInfo);
    return acc;
  }, {}), [extractedDates]);

  const { allYearsSet, years } = useMemo(() => {
    const set = new Set([...Object.keys(groupedByYear), ...Object.keys(groupedExtractedDates)]);
    return { allYearsSet: set, years: Array.from(set).sort((a, b) => b - a) };
  }, [groupedByYear, groupedExtractedDates]);
  
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  // Memoize selected day entries to avoid recalculation on every render
  const selectedDayEntries = useMemo(() => {
    if (selectedDay === null) return [];
    return (groupedByYear[calendarYear]?.[calendarMonth] || []).filter(e => new Date(e.timestamp).getDate() === selectedDay);
  }, [selectedDay, calendarYear, calendarMonth, groupedByYear]);

  const handleYearClick = (year) => { setSelectedYear(year); setSelectedMonth(null); setViewMode('list'); setSelectedDay(null); };
  const handleMonthClick = (month) => { setSelectedMonth(month); setViewMode('list'); setSelectedDay(null); };
  const handleBack = () => { 
    if (selectedDay !== null) { 
      setSelectedDay(null); 
    } else if (selectedMonth !== null) { 
      // If we came from calendar year view, go back to it
      if (timelineView === 'calendar') {
        setSelectedMonth(null);
        setSelectedYear(null);
        setViewMode('list');
      } else {
        setSelectedMonth(null); 
        setViewMode('list');
      }
    } else if (selectedYear !== null) { 
      setSelectedYear(null); 
    } 
  };

  // Handle creating entry for empty day
  const handleEmptyDayClick = (day) => {
    setCreateEntryDay(day);
    setCreateEntryType(null);
    setCreateEntryContent('');
  };

  const handleCreateEntry = async () => {
    if (!createEntryContent.trim() || !createEntryType || !createEntryDay) return;
    
    setIsCreatingEntry(true);
    try {
      // Create date for the selected day
      const entryDate = new Date(selectedYear, selectedMonth, createEntryDay, 12, 0, 0);
      
      if (createEntryType === 'journal') {
        // Create journal entry with custom date
        await axios.post(`${API}/journal_entries`, {
          content: createEntryContent,
          customDate: entryDate.toISOString()
        });
      } else if (createEntryType === 'story') {
        // Create story in Unsorted Stories
        await axios.post(`${API}/experiences`, {
          summary: createEntryContent,
          topic_folder: 'Unsorted Stories',
          timestamp: entryDate.toISOString()
        });
      }
      
      // Refresh data
      await fetchData();
      
      // Close modal
      setCreateEntryDay(null);
      setCreateEntryType(null);
      setCreateEntryContent('');
    } catch (error) {
      console.error('Error creating entry:', error);
    } finally {
      setIsCreatingEntry(false);
    }
  };

  const currentEntries = selectedMonth !== null && selectedYear !== null ? groupedByYear[selectedYear]?.[selectedMonth] || [] : [];
  const currentExtractedDates = selectedMonth !== null && selectedYear !== null ? groupedExtractedDates[selectedYear]?.[selectedMonth] || [] : [];

  // Generate calendar data for the selected month
  const calendarData = useMemo(() => {
    if (selectedMonth === null || selectedYear === null) return null;
    
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const firstDayOfWeek = new Date(selectedYear, selectedMonth, 1).getDay();
    
    // Create a map of day -> entries
    const entriesByDay = {};
    currentEntries.forEach(entry => {
      const day = new Date(entry.timestamp).getDate();
      if (!entriesByDay[day]) entriesByDay[day] = [];
      entriesByDay[day].push(entry);
    });
    
    return { daysInMonth, firstDayOfWeek, entriesByDay };
  }, [selectedMonth, selectedYear, currentEntries]);

  // Get entries for selected day - use memoized version instead
  // const selectedDayEntries = selectedDay && calendarData?.entriesByDay[selectedDay] || [];

  // Year calendar data: for each month, count entries per day
  const yearCalendarData = useMemo(() => {
    const data = {};
    months.forEach((_, monthIdx) => {
      const monthEntries = groupedByYear[calendarYear]?.[monthIdx] || [];
      const daysInMonth = new Date(calendarYear, monthIdx + 1, 0).getDate();
      const firstDayOfWeek = new Date(calendarYear, monthIdx, 1).getDay();
      const entriesByDay = {};
      monthEntries.forEach(entry => {
        const day = new Date(entry.timestamp).getDate();
        if (!entriesByDay[day]) entriesByDay[day] = [];
        entriesByDay[day].push(entry);
      });
      data[monthIdx] = { daysInMonth, firstDayOfWeek, entriesByDay, totalEntries: monthEntries.length };
    });
    return data;
  }, [calendarYear, groupedByYear]);

  // Full calendar view data for the selected calendarMonth
  const fullCalendarData = useMemo(() => {
    const monthEntries = groupedByYear[calendarYear]?.[calendarMonth] || [];
    const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
    const firstDayOfWeek = new Date(calendarYear, calendarMonth, 1).getDay();
    const entriesByDay = {};
    monthEntries.forEach(entry => {
      const day = new Date(entry.timestamp).getDate();
      if (!entriesByDay[day]) entriesByDay[day] = [];
      entriesByDay[day].push(entry);
    });
    return { daysInMonth, firstDayOfWeek, entriesByDay, totalEntries: monthEntries.length };
  }, [calendarYear, calendarMonth, groupedByYear]);

  const navigateCalendarMonth = (dir) => {
    if (dir === -1) {
      if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(y => y - 1); }
      else setCalendarMonth(m => m - 1);
    } else {
      if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(y => y + 1); }
      else setCalendarMonth(m => m + 1);
    }
    setSelectedDay(null);
  };

  // Mobile swipe to change months
  const handleCalendarTouchStart = useCallback((e) => {
    // Ignore multi-touch (two+ fingers)
    if (e.touches.length > 1) { calendarTouchRef.current = null; return; }
    calendarTouchRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY };
  }, []);

  const handleCalendarTouchEnd = useCallback((e) => {
    if (!calendarTouchRef.current) return;
    const dx = e.changedTouches[0].clientX - calendarTouchRef.current.startX;
    const dy = e.changedTouches[0].clientY - calendarTouchRef.current.startY;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      navigateCalendarMonth(dx > 0 ? -1 : 1);
    }
  }, [calendarMonth, calendarYear]);

  const handleShareEntry = async () => {
    if (!shareEmail.trim() || !shareEntryId) return;
    setShareLoading(true);
    setShareError('');
    setShareSuccess('');
    try {
      await axios.post(`${BACKEND_URL}/api/entries/${shareEntryId}/share`, { email: shareEmail.trim().toLowerCase() });
      setShareSuccess('Entry shared successfully!');
      setTimeout(() => { setShareEntryId(null); setShareEmail(''); setShareSuccess(''); }, 1500);
    } catch (err) {
      setShareError(err.response?.data?.detail || 'Failed to share');
    } finally {
      setShareLoading(false);
    }
  };


  // Close year picker on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (yearPickerRef.current && !yearPickerRef.current.contains(e.target)) setShowYearPicker(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Generate available years
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 10; y--) years.push(y);
    return years;
  }, []);

  // Auto-scroll to center the current month on load
  useEffect(() => {
    if (!loading && timelineView === 'calendar' && !hasScrolledToCenter && scrollContainerRef.current) {
      // Small delay to let the DOM render fully
      requestAnimationFrame(() => {
        const el = scrollContainerRef.current?.querySelector('[data-center="true"]');
        if (el) {
          el.scrollIntoView({ behavior: 'instant', inline: 'center', block: 'nearest' });
          setHasScrolledToCenter(true);
        }
      });
    }
  }, [loading, timelineView, hasScrolledToCenter]);

  return (
    <motion.div variants={getPageVariants(direction)} initial="initial" animate="animate" exit="exit" transition={pageTransition} className="min-h-screen pt-20 md:pt-24 pb-4">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="mb-8 md:mb-12">
          {(selectedYear || selectedMonth !== null) && (
            <div className="flex items-center space-x-4 mb-6">
              <button onClick={handleBack} className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors" data-testid="timeline-back-button" title="Go back">
                <ChevronRight className="w-4 h-4 rotate-180" /><span>Back</span>
              </button>
            </div>
          )}
          <div className="flex items-center justify-between mb-1 md:mb-2">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight text-foreground" data-testid="timeline-page-title">
              {selectedMonth !== null ? `${months[selectedMonth]} ${selectedYear}` : selectedYear || 'Timeline'}
            </h1>
            {/* Calendar View button */}
            {!selectedYear && !selectedMonth && (
              <button
                onClick={() => { setTimelineView(v => v === 'calendar' ? 'list' : 'calendar'); setSelectedDay(null); }}
                className={`p-2.5 rounded-full border transition-all ${
                  timelineView === 'calendar' 
                    ? 'bg-primary text-white border-primary' 
                    : 'bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                }`}
                data-testid="timeline-calendar-view-btn"
                title="Calendar View"
              >
                <Calendar className="w-5 h-5" />
              </button>
            )}
          </div>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {!selectedYear && 'Browse your journal entries by year and month'}
            {selectedYear && !selectedMonth && `Select a month from ${selectedYear}`}
            {selectedMonth !== null && `${currentEntries.length} ${currentEntries.length === 1 ? 'entry' : 'entries'}`}
          </p>
        </div>

        {/* Full Calendar View - infinite horizontally scrollable months */}
        {!selectedYear && !loading && timelineView === 'calendar' && (() => {
          const now = new Date();
          const currentY = now.getFullYear();
          const currentM = now.getMonth();
          const RANGE = 36;
          const allMonths = [];
          for (let offset = -RANGE; offset <= RANGE; offset++) {
            let m = currentM + offset;
            let y = currentY;
            while (m < 0) { m += 12; y--; }
            while (m > 11) { m -= 12; y++; }
            allMonths.push({ year: y, month: m, offset });
          }
          const centerIndex = RANGE;

          return (
          <div data-testid="year-calendar-view">
            {/* Year heading — big and anchored */}
            <div className="flex items-center justify-between mb-4">
              <div className="relative inline-block" ref={yearPickerRef}>
                <button
                  onClick={() => setShowYearPicker(!showYearPicker)}
                  className="text-5xl md:text-6xl font-serif font-semibold text-foreground flex items-center gap-3 transition-colors hover:text-primary tracking-tight"
                  data-testid="calendar-year-picker-btn"
                >
                  {calendarYear} <ChevronDown className={`w-6 h-6 transition-transform ${showYearPicker ? 'rotate-180' : ''}`} />
                </button>
                {showYearPicker && (
                  <div className="absolute top-full mt-2 left-0 bg-card-solid border border-border rounded-xl shadow-lg py-1 z-20 max-h-48 overflow-y-auto min-w-[120px]" data-testid="calendar-year-picker-menu">
                    {availableYears.map(y => (
                      <button
                        key={y}
                        onClick={() => {
                          setCalendarYear(y);
                          setShowYearPicker(false);
                          setSelectedDay(null);
                          const target = allMonths.findIndex(am => am.year === y && am.month === 0);
                          if (target >= 0 && scrollContainerRef.current) {
                            const child = scrollContainerRef.current.children[0]?.children[target];
                            if (child) child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                          }
                        }}
                        className={`w-full px-5 py-2 text-base text-center transition-colors ${y === calendarYear ? 'text-primary font-medium bg-primary/5' : 'text-foreground hover:bg-secondary/50'}`}
                        data-testid={`year-option-${y}`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Calendar with navigation arrows */}
            <div className="relative">
              {/* Left arrow */}
              <button
                onClick={() => {
                  if (!scrollContainerRef.current) return;
                  const container = scrollContainerRef.current;
                  const monthWidth = container.children[0]?.children[0]?.offsetWidth || container.clientWidth;
                  container.scrollBy({ left: -monthWidth, behavior: 'smooth' });
                }}
                className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-14 h-14 items-center justify-center rounded-full bg-card-solid border border-border shadow-lg text-muted-foreground hover:text-primary hover:border-primary/50 transition-all hover:shadow-xl cursor-pointer"
                data-testid="calendar-nav-left"
              >
                <ChevronLeft className="w-7 h-7" />
              </button>

              {/* Right arrow */}
              <button
                onClick={() => {
                  if (!scrollContainerRef.current) return;
                  const container = scrollContainerRef.current;
                  const monthWidth = container.children[0]?.children[0]?.offsetWidth || container.clientWidth;
                  container.scrollBy({ left: monthWidth, behavior: 'smooth' });
                }}
                className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-14 h-14 items-center justify-center rounded-full bg-card-solid border border-border shadow-lg text-muted-foreground hover:text-primary hover:border-primary/50 transition-all hover:shadow-xl cursor-pointer"
                data-testid="calendar-nav-right"
              >
                <ChevronRight className="w-7 h-7" />
              </button>

              {/* Horizontally scrollable months — grab cursor, infinite */}
              <div
              ref={scrollContainerRef}
              className="overflow-x-auto overflow-y-hidden -mx-6 scrollbar-hide select-none"
              style={{ WebkitOverflowScrolling: 'touch', cursor: isDragging ? 'grabbing' : 'grab' }}
              onMouseDown={(e) => {
                setIsDragging(true);
                dragRef.current = { startX: e.pageX, scrollLeft: scrollContainerRef.current.scrollLeft };
              }}
              onMouseMove={(e) => {
                if (!isDragging || !dragRef.current) return;
                e.preventDefault();
                scrollContainerRef.current.scrollLeft = dragRef.current.scrollLeft - (e.pageX - dragRef.current.startX);
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onScroll={() => {
                if (!scrollContainerRef.current) return;
                const container = scrollContainerRef.current;
                const centerX = container.scrollLeft + container.clientWidth / 2;
                const inner = container.children[0];
                if (!inner) return;
                for (let i = 0; i < inner.children.length; i++) {
                  const child = inner.children[i];
                  if (centerX >= child.offsetLeft && centerX < child.offsetLeft + child.offsetWidth) {
                    const ym = allMonths[i];
                    if (ym && ym.year !== calendarYear) setCalendarYear(ym.year);
                    break;
                  }
                }
              }}
              data-testid="calendar-scroll-container"
            >
              <div className="flex items-start" style={{ width: 'max-content' }}>
                {allMonths.map((am, idx) => {
                  const y = am.year;
                  const mi = am.month;
                  const monthEntries = groupedByYear[y]?.[mi] || [];
                  const daysInMonth = new Date(y, mi + 1, 0).getDate();
                  const firstDayOfWeek = new Date(y, mi, 1).getDay();
                  const entriesByDay = {};
                  monthEntries.forEach(entry => {
                    const day = new Date(entry.timestamp).getDate();
                    if (!entriesByDay[day]) entriesByDay[day] = [];
                    entriesByDay[day].push(entry);
                  });
                  const isCurrent = y === currentY && mi === currentM;

                  return (
                    <div
                      key={`${y}-${mi}`}
                      className="flex-shrink-0 px-6"
                      style={{ width: 'calc(100vw - 48px)', maxWidth: '900px', scrollSnapAlign: 'center' }}
                      data-testid={`calendar-month-${y}-${mi}`}
                      data-center={idx === centerIndex ? 'true' : undefined}
                    >
                      <div className="flex items-baseline gap-2 mb-2">
                        <h3 className={`text-2xl md:text-3xl font-serif font-medium ${isCurrent ? 'text-primary' : 'text-foreground'}`}>
                          {months[mi]}
                        </h3>
                        {monthEntries.length > 0 && (
                          <span className="text-sm text-muted-foreground">{monthEntries.length} {monthEntries.length === 1 ? 'entry' : 'entries'}</span>
                        )}
                      </div>

                      <div className="grid grid-cols-7 gap-1.5 mb-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
                          <div key={i} className="text-center text-xs md:text-sm text-muted-foreground font-medium">{d}</div>
                        ))}
                      </div>

                      <div className="grid grid-cols-7 gap-1.5">
                        {[...Array(firstDayOfWeek)].map((_, i) => (
                          <div key={`e-${i}`} className="aspect-square" />
                        ))}
                        {[...Array(daysInMonth)].map((_, i) => {
                          const day = i + 1;
                          const dayEnts = entriesByDay[day] || [];
                          const count = dayEnts.length;
                          const has = count > 0;
                          const isToday = isCurrent && day === now.getDate();
                          const isSelected = selectedDay === day && calendarMonth === mi && calendarYear === y;

                          return (
                            <button
                              key={day}
                              onClick={() => {
                                if (has) {
                                  setCalendarYear(y);
                                  setCalendarMonth(mi);
                                  setSelectedDay(isSelected ? null : day);
                                  if (!isSelected) {
                                    setTimeout(() => {
                                      if (entriesRef.current) {
                                        const rect = entriesRef.current.getBoundingClientRect();
                                        const offset = window.scrollY + rect.top - 80;
                                        window.scrollTo({ top: offset, behavior: 'smooth' });
                                      }
                                    }, 100);
                                  }
                                } else {
                                  // Empty day - show create options
                                  setCalendarYear(y);
                                  setCalendarMonth(mi);
                                  setSelectedYear(y);
                                  setSelectedMonth(mi);
                                  handleEmptyDayClick(day);
                                }
                              }}
                              className={`aspect-square rounded-xl border flex flex-col items-center justify-center transition-all group relative ${
                                isSelected
                                  ? 'bg-primary text-white border-primary shadow-lg'
                                  : isToday
                                    ? 'bg-primary/10 dark:bg-primary/40 border-primary/40 dark:border-primary text-primary font-bold hover:bg-primary hover:text-white hover:border-primary hover:shadow-lg cursor-pointer'
                                    : has
                                      ? 'bg-card dark:bg-primary/25 border-border dark:border-primary/50 text-primary hover:bg-primary dark:hover:bg-primary/60 hover:text-white hover:border-primary hover:shadow-lg cursor-pointer'
                                      : 'bg-secondary/20 border-border/30 text-muted-foreground/40 hover:bg-primary/10 hover:border-primary/30 hover:text-primary cursor-pointer'
                              }`}
                              data-testid={`calendar-day-${y}-${mi}-${day}`}
                            >
                              <span className={`text-lg md:text-xl font-serif font-semibold leading-none ${isSelected ? 'text-white' : has ? 'group-hover:text-white' : 'group-hover:text-primary'}`}>{day}</span>
                              {has ? (
                                <div className="flex gap-0.5 mt-1">
                                  {[...Array(Math.min(count, 3))].map((_, di) => (
                                    <span key={di} className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white' : 'bg-primary group-hover:bg-white'}`} />
                                  ))}
                                </div>
                              ) : (
                                <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-primary absolute bottom-1" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            </div>

            {selectedDay && (
              <motion.div ref={entriesRef} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-serif font-medium text-foreground">{months[calendarMonth]} {selectedDay}, {calendarYear} — {selectedDayEntries.length} {selectedDayEntries.length === 1 ? 'entry' : 'entries'}</h3>
                  <button onClick={() => setSelectedDay(null)} className="text-sm text-muted-foreground hover:text-primary transition-colors" title="Hide entries">Hide</button>
                </div>
                {selectedDayEntries.map(entry => {
                  const ts = new Date(entry.timestamp);
                  const wordCount = entry.content ? entry.content.trim().split(/\s+/).length : 0;
                  return (
                  <div key={entry.id} className="p-4 bg-card border border-border rounded-2xl hover:border-primary/50 transition-all" data-testid={`cal-entry-${entry.id}`}>
                    {/* Header: Date box + actions */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20">
                          <span className="text-lg font-serif font-bold text-primary">{ts.getDate()}</span>
                          <span className="text-[8px] text-primary uppercase tracking-wider">{ts.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{ts.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs text-muted-foreground">{ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</span>
                            {entry.has_audio && <Mic className="w-3 h-3 text-accent" />}
                            {wordCount > 0 && <span className="text-xs text-muted-foreground">{wordCount} words</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button onClick={(e) => { e.stopPropagation(); setShareEntryId(entry.id); setShareEmail(''); setShareError(''); setShareSuccess(''); }} className="p-2 rounded-lg text-muted-foreground hover:text-accent hover:bg-accent/10 transition-colors" data-testid={`share-entry-${entry.id}`} title="Share with NOYO user">
                          <Users className="w-4 h-4" />
                        </button>
                        <button onClick={() => navigate(`/entry/${entry.id}/edit`)} className="text-sm text-primary hover:text-[#7A9186] transition-colors flex items-center space-x-1" data-testid={`edit-entry-${entry.id}`} title="Edit entry">
                          <span>Edit</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {/* Content */}
                    <p className="text-base md:text-xl lg:text-2xl leading-loose md:leading-[2.5] font-serif text-foreground whitespace-pre-wrap">{entry.content}</p>
                  </div>
                  );
                })}
              </motion.div>
            )}
          </div>
          );
        })()}

        {!selectedYear && timelineView === 'list' && (
          loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="loading-skeleton">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="p-6 rounded-2xl bg-card border border-border animate-pulse">
                  <div className="h-8 bg-muted rounded w-24 mb-3"></div>
                  <div className="h-4 bg-muted rounded w-16"></div>
                </div>
              ))}
            </div>
          ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="years-grid">
            {years.map(year => {
              const yearEntries = groupedByYear[year] ? Object.values(groupedByYear[year]).flat() : [];
              const yearExtracted = groupedExtractedDates[year] ? Object.values(groupedExtractedDates[year]).flat() : [];
              return (
                <motion.button key={year} onClick={() => handleYearClick(year)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg text-left" data-testid={`year-button-${year}`}>
                  <h2 className="text-3xl font-serif font-medium text-foreground mb-2">{year}</h2>
                  <p className="text-sm text-muted-foreground">{yearEntries.length} {yearEntries.length === 1 ? 'entry' : 'entries'}</p>
                  {yearExtracted.length > 0 && <p className="text-xs text-accent font-medium mt-1">+ {yearExtracted.length} date {yearExtracted.length === 1 ? 'reference' : 'references'}</p>}
                </motion.button>
              );
            })}
          </div>
          )
        )}

        {selectedYear && selectedMonth === null && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="months-grid">
            {months.map((month, index) => {
              const monthEntries = groupedByYear[selectedYear]?.[index] || [];
              const monthExtracted = groupedExtractedDates[selectedYear]?.[index] || [];
              if (monthEntries.length === 0 && monthExtracted.length === 0) return null;
              return (
                <motion.button key={index} onClick={() => handleMonthClick(index)} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="p-6 rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg text-left" data-testid={`month-button-${index}`}>
                  <h3 className="text-2xl font-serif font-medium text-foreground mb-2">{month}</h3>
                  <p className="text-sm text-muted-foreground">{monthEntries.length} {monthEntries.length === 1 ? 'entry' : 'entries'}</p>
                  {monthExtracted.length > 0 && <p className="text-xs text-accent font-medium mt-1">+ {monthExtracted.length} date {monthExtracted.length === 1 ? 'reference' : 'references'}</p>}
                </motion.button>
              );
            })}
          </div>
        )}

        {selectedMonth !== null && selectedYear && (
          <div data-testid="entries-view">
            {/* View Toggle */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-serif font-medium text-foreground flex items-center space-x-2">
                <BookOpen className="w-5 h-5 text-primary" />
                <span>{selectedDay ? `${months[selectedMonth]} ${selectedDay}` : 'Journal Entries'}</span>
              </h3>
              <div className="flex items-center gap-1 bg-secondary/50 rounded-xl p-1">
                <button
                  onClick={() => { setViewMode('list'); setSelectedDay(null); }}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="view-mode-list"
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setViewMode('calendar'); setSelectedDay(null); }}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'calendar' ? 'bg-card shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid="view-mode-calendar"
                  title="Calendar view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Calendar Tile View */}
            {viewMode === 'calendar' && calendarData && !selectedDay && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6"
                data-testid="calendar-tile-view"
              >
                {/* Day labels */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                    <div key={day} className="text-center text-xs text-muted-foreground font-medium py-1">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells for days before the first day of month */}
                  {[...Array(calendarData.firstDayOfWeek)].map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ))}
                  
                  {/* Day tiles */}
                  {[...Array(calendarData.daysInMonth)].map((_, i) => {
                    const day = i + 1;
                    const hasEntries = calendarData.entriesByDay[day]?.length > 0;
                    const entryCount = calendarData.entriesByDay[day]?.length || 0;
                    
                    return (
                      <motion.button
                        key={day}
                        onClick={() => hasEntries ? setSelectedDay(day) : handleEmptyDayClick(day)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`aspect-square rounded-xl flex flex-col items-center justify-center transition-all cursor-pointer group ${
                          hasEntries 
                            ? 'bg-primary/10 dark:bg-primary/35 border-2 border-primary/30 dark:border-primary/60 hover:border-primary hover:bg-primary/20 dark:hover:bg-primary/50 dark:hover:border-primary' 
                            : 'bg-secondary/30 border border-border/50 hover:border-primary/50 hover:bg-secondary/50 opacity-60 hover:opacity-100'
                        }`}
                        data-testid={`calendar-day-${day}`}
                      >
                        {hasEntries ? (
                          <>
                            <span className="text-lg font-serif font-semibold text-primary dark:text-primary">
                              {day}
                            </span>
                            <span className="w-1.5 h-1.5 rounded-full bg-primary dark:bg-primary mt-0.5" />
                          </>
                        ) : (
                          <>
                            <span className="text-lg font-serif font-semibold text-muted-foreground group-hover:hidden">
                              {day}
                            </span>
                            <Plus className="w-5 h-5 text-primary hidden group-hover:block" />
                          </>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Selected Day Entries (from calendar view) */}
            {viewMode === 'calendar' && selectedDay && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={`day-${selectedDay}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                  data-testid="selected-day-entries"
                >
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-4"
                    data-testid="back-to-calendar"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                    <span>Back to calendar</span>
                  </button>
                  {selectedDayEntries.map(entry => {
                    const entryDate = new Date(entry.timestamp);
                    const isExpanded = expandedEntries[entry.id];
                    const needsTruncation = entry.content && entry.content.length > MAX_PREVIEW_LENGTH;
                    return (
                      <motion.div key={entry.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`p-4 md:p-6 rounded-2xl bg-card border transition-all duration-300 hover:shadow-lg ${highlightedEntryId === entry.id ? 'border-primary shadow-lg shadow-primary/20 ring-2 ring-primary/30 animate-pulse' : 'border-border hover:border-primary/50'}`} data-testid={`timeline-entry-${entry.id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20">
                              <span className="text-lg font-serif font-bold text-primary">{entryDate.getDate()}</span>
                              <span className="text-[8px] text-primary uppercase tracking-wider">{entryDate.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{entryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                              <p className="text-xs text-muted-foreground">{entryDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                            </div>
                          </div>
                          <button onClick={() => navigate(`/entry/${entry.id}/edit`)} className="text-sm text-primary hover:text-[#7A9186] transition-colors flex items-center space-x-1" data-testid={`view-entry-${entry.id}`} title="Edit entry"><span>Edit</span><ChevronRight className="w-4 h-4" /></button>
                        </div>
                        <div className="mt-3">
                          {entry.content === "Voice Recording" ? (
                            <div className="flex items-center space-x-2 text-sm md:text-base text-foreground"><Mic className="w-4 h-4 text-muted-foreground" /><span className="font-serif">Voice Recording</span></div>
                          ) : (
                            <div>
                              <p className="text-sm md:text-base leading-relaxed font-serif text-foreground whitespace-pre-wrap">{isExpanded || !needsTruncation ? entry.content : `${entry.content.slice(0, MAX_PREVIEW_LENGTH)}...`}</p>
                              {needsTruncation && <button onClick={() => toggleEntryExpansion(entry.id)} className="mt-2 text-sm text-primary hover:text-[#7A9186] transition-colors font-medium" data-testid={`toggle-timeline-entry-${entry.id}`} title={isExpanded ? 'Show less' : 'See more'}>{isExpanded ? 'Show less' : 'See more'}</button>}
                            </div>
                          )}
                        </div>
                        {entry.has_audio && !entry.audio_missing && (<div className="mt-3 pt-3 border-t border-border"><AudioErrorBoundary><AudioPlayer entryId={entry.id} /></AudioErrorBoundary></div>)}
                        {entry.has_audio && entry.audio_missing && (<div className="mt-3 pt-3 border-t border-border"><p className="text-xs text-muted-foreground">Audio file no longer available</p></div>)}
                      </motion.div>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            )}

            {/* List View */}
            {viewMode === 'list' && (
              <div className="space-y-4 md:space-y-6" data-testid="entries-grid">
                {currentEntries.map(entry => {
                  const entryDate = new Date(entry.timestamp);
                  const isExpanded = expandedEntries[entry.id];
                  const needsTruncation = entry.content && entry.content.length > MAX_PREVIEW_LENGTH;
                  return (
                    <motion.div key={entry.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`p-4 md:p-6 rounded-2xl bg-card border transition-all duration-300 hover:shadow-lg mb-4 ${highlightedEntryId === entry.id ? 'border-primary shadow-lg shadow-primary/20 ring-2 ring-primary/30 animate-pulse' : 'border-border hover:border-primary/50'}`} data-testid={`timeline-entry-${entry.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="flex flex-col items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20">
                            <span className="text-lg font-serif font-bold text-primary">{entryDate.getDate()}</span>
                            <span className="text-[8px] text-primary uppercase tracking-wider">{entryDate.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{entryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                            <p className="text-xs text-muted-foreground">{entryDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button onClick={() => navigate(`/entry/${entry.id}/edit`)} className="text-sm text-primary hover:text-[#7A9186] transition-colors flex items-center space-x-1" data-testid={`view-entry-${entry.id}`} title="Edit entry"><span>Edit</span><ChevronRight className="w-4 h-4" /></button>
                          <button onClick={() => { setShareEntry(entry); setShareEmail(''); setShareError(''); setShareSuccess(''); }} className="p-2 rounded-full bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg transition-all" data-testid={`share-entry-btn-${entry.id}`} title="Share Entry"><UserPlus className="w-4 h-4 stroke-[2.5]" /></button>
                        </div>
                      </div>
                      <div className="mt-3">
                        {entry.content === "Voice Recording" ? (
                          <div className="flex items-center space-x-2 text-sm md:text-base text-foreground"><Mic className="w-4 h-4 text-muted-foreground" /><span className="font-serif">Voice Recording</span></div>
                        ) : (
                          <div>
                            <p className="text-sm md:text-base leading-relaxed font-serif text-foreground whitespace-pre-wrap">{isExpanded || !needsTruncation ? entry.content : `${entry.content.slice(0, MAX_PREVIEW_LENGTH)}...`}</p>
                            {needsTruncation && <button onClick={() => toggleEntryExpansion(entry.id)} className="mt-2 text-sm text-primary hover:text-[#7A9186] transition-colors font-medium" data-testid={`toggle-timeline-entry-${entry.id}`}>{isExpanded ? 'Show less' : 'See more'}</button>}
                          </div>
                        )}
                      </div>
                      {entry.has_audio && !entry.audio_missing && (<div className="mt-3 pt-3 border-t border-border"><AudioErrorBoundary><AudioPlayer entryId={entry.id} /></AudioErrorBoundary></div>)}
                      {entry.has_audio && entry.audio_missing && (<div className="mt-3 pt-3 border-t border-border"><p className="text-xs text-muted-foreground">Audio file no longer available</p></div>)}
                      
                      {/* Attached Images */}
                      {((entry.images?.length > 0) || (entry.image_filenames?.length > 0)) && (
                        <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid={`timeline-images-${entry.id}`}>
                          {(entry.images?.length > 0 ? entry.images : entry.image_filenames).map((_, idx) => {
                            const thumbUrl = getImageUrl(entry, idx, 'thumb');
                            const fullUrl = getImageUrl(entry, idx, 'full');
                            const downloadUrl = getDownloadUrl(entry, idx);
                            if (!thumbUrl) return null;
                            return (
                              <div key={idx} className="relative group cursor-pointer" onClick={() => setLightboxImage({ imageUrl: fullUrl, downloadUrl: downloadUrl })}>
                                <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden border border-border shadow-sm hover:shadow-md transition-shadow bg-secondary/30">
                                  <img src={thumbUrl} alt="" loading="lazy" className="w-full h-full object-cover" data-testid={`timeline-image-${entry.id}-${idx}`} />
                                </div>
                                <a href={downloadUrl || '#'} onClick={(e) => e.stopPropagation()} className="absolute bottom-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Download Original"><Download className="w-3 h-3" /></a>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      
                      {entry.has_audio && !entry.is_transcribed && !entry.audio_missing && (
                        <div className="mt-2 flex justify-end">
                          <button onClick={() => handleTranscribeEntry(entry.id)} disabled={transcribingId === entry.id} className="text-xs bg-accent/10 text-accent hover:bg-accent/20 rounded-full px-3 py-1.5 font-medium transition-colors disabled:opacity-50 flex items-center space-x-1" data-testid={`timeline-transcribe-${entry.id}`}>
                            {transcribingId === entry.id ? (<><Sparkles className="w-3 h-3 animate-pulse" /><span>Transcribing...</span></>) : (<><Sparkles className="w-3 h-3" /><span>Transcribe</span></>)}
                          </button>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
            {currentExtractedDates.length > 0 && (
              <div>
                <h3 className="text-lg font-serif font-medium text-foreground mb-3 flex items-center space-x-2"><Calendar className="w-5 h-5 text-accent" /><span>Date References from Entries</span></h3>
                {currentExtractedDates.map((dateRef, idx) => (
                  <motion.div key={`${dateRef.entry_id}-${idx}`} whileHover={{ x: 4 }} className="flex items-start space-x-4 p-4 rounded-2xl bg-accent/5 border border-accent/20 hover:border-accent/50 transition-all duration-300 hover:shadow-lg cursor-pointer mb-2" onClick={() => navigate(`/?entry=${dateRef.entry_id}`)} data-testid={`date-ref-button-${idx}`}>
                    <div className="flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-xl bg-accent/10 border border-accent/30">
                      <span className="text-2xl font-serif font-bold text-accent">{dateRef.day || '~'}</span>
                      <span className="text-[10px] text-accent uppercase tracking-wider">REF</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-accent mb-1">{dateRef.date_text}</p>
                      <p className="text-sm text-foreground/80 italic line-clamp-1 mb-1">"{dateRef.context}"</p>
                      <p className="text-xs text-muted-foreground">From: {dateRef.entry_preview}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-accent flex-shrink-0 self-center" />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {years.length === 0 && (
          <div className="text-center py-12"><Calendar className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" /><p className="text-muted-foreground italic">No journal entries yet. Start writing to see your timeline!</p></div>
        )}
      </div>
      
      {/* Image Lightbox */}
      {lightboxImage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[200] p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="timeline-image-lightbox"
        >
          <button onClick={() => setLightboxImage(null)} className="absolute top-4 right-4 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center">
            <X className="w-6 h-6" />
          </button>
          {lightboxImage.downloadUrl && (
            <a href={lightboxImage.downloadUrl} onClick={(e) => e.stopPropagation()} className="absolute top-4 left-4 text-white/80 hover:text-white bg-black/40 rounded-full w-10 h-10 flex items-center justify-center" title="Download">
              <Download className="w-5 h-5" />
            </a>
          )}
          <img src={lightboxImage.imageUrl} alt="" className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </motion.div>
      )}

      {/* Share Entry Modal */}
      {shareEntryId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShareEntryId(null)} data-testid="share-entry-modal">
          <div className="bg-card-solid rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Users className="w-5 h-5 text-accent" />
                <h3 className="text-lg font-serif font-medium">Share Journal Entry</h3>
              </div>
              <button onClick={() => setShareEntryId(null)} className="p-1 rounded-full hover:bg-secondary transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-muted-foreground mb-4">Share this entry with another NOYO user. It will appear in their "Shared with Me" folder.</p>
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleShareEntry()}
              placeholder="Enter their email address"
              className="w-full px-4 py-3 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 mb-3"
              autoFocus
              data-testid="share-entry-email-input"
            />
            {shareError && <p className="text-sm text-red-500 mb-2" data-testid="share-entry-error">{shareError}</p>}
            {shareSuccess && <p className="text-sm text-green-600 mb-2" data-testid="share-entry-success">{shareSuccess}</p>}
            <button
              onClick={handleShareEntry}
              disabled={shareLoading || !shareEmail.trim()}
              className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-[#7A9186] transition-colors disabled:opacity-50"
              data-testid="share-entry-submit"
            >
              {shareLoading ? 'Sharing...' : 'Share Entry'}
            </button>
          </div>
        </div>
      )}

      {/* Create Entry Modal */}
      {createEntryDay !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setCreateEntryDay(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card-solid rounded-2xl p-6 w-full max-w-md shadow-xl"
            data-testid="create-entry-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif font-semibold text-foreground">
                Create Entry for {months[selectedMonth]} {createEntryDay}, {selectedYear}
              </h3>
              <button onClick={() => setCreateEntryDay(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>

            {!createEntryType ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground mb-4">What would you like to create?</p>
                <button
                  onClick={() => setCreateEntryType('journal')}
                  className="w-full p-4 bg-secondary/50 hover:bg-secondary border border-border hover:border-primary/50 rounded-xl text-left transition-all group"
                  data-testid="create-journal-entry-btn"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <span className="font-medium text-foreground block">Journal Entry</span>
                      <span className="text-xs text-muted-foreground">Write a personal reflection or note</span>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setCreateEntryType('story')}
                  className="w-full p-4 bg-secondary/50 hover:bg-secondary border border-border hover:border-primary/50 rounded-xl text-left transition-all group"
                  data-testid="create-story-btn"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <span className="font-medium text-foreground block">Story</span>
                      <span className="text-xs text-muted-foreground">Create a story for your collection</span>
                    </div>
                  </div>
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-2">
                  <button 
                    onClick={() => setCreateEntryType(null)}
                    className="text-sm text-muted-foreground hover:text-primary flex items-center"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Back
                  </button>
                  <span className="text-sm font-medium text-foreground">
                    {createEntryType === 'journal' ? 'New Journal Entry' : 'New Story'}
                  </span>
                </div>
                <textarea
                  value={createEntryContent}
                  onChange={(e) => setCreateEntryContent(e.target.value)}
                  placeholder={createEntryType === 'journal' ? "Write your journal entry..." : "Write your story..."}
                  className="w-full min-h-[150px] p-4 bg-secondary/30 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none font-serif"
                  autoFocus
                  data-testid="create-entry-textarea"
                />
                <button
                  onClick={handleCreateEntry}
                  disabled={isCreatingEntry || !createEntryContent.trim()}
                  className="w-full py-3 bg-primary text-white rounded-xl font-medium hover:bg-primary/80 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  data-testid="save-entry-btn"
                >
                  <Save className="w-4 h-4" />
                  <span>{isCreatingEntry ? 'Saving...' : 'Save Entry'}</span>
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

    </motion.div>
  );
}

export default TimelinePage;
